import {observe} from '@github/selector-observer'
import {on} from 'delegated-events'
import {remoteForm} from '@github-ui/remote-form'
import {requestSubmit} from '@github-ui/form-utils'
import {updateSearchParams} from '@github-ui/history'

on('remote-input-error', '#js-codespaces-repository-select', () => {
  const warning = document.querySelector<HTMLElement>('#js-codespaces-unable-load-repositories-warning')!
  warning.hidden = false
})

function updateNewCodespaceUrl(form: FormData): void {
  const url = new URL(document.location.href, window.location.origin)
  const urlParams = new URLSearchParams(url.search)
  // Certain form name/values we don't want to save as these are very ENV specific.
  // They can cause problems in different environments (e.g. review lab, local, production, etc...)
  // Add those keys to this list as needed.
  const paramsKeysToReject = ['vscs_target']

  for (const [key, value] of form.entries()) {
    if (paramsKeysToReject.includes(key) || !value) {
      urlParams.delete(key)
      continue
    }

    urlParams.set(key, value as string)
  }

  updateSearchParams(urlParams)
}

remoteForm('.js-new-codespace-form', async function (form, wants) {
  const container = form.closest<HTMLElement>('[data-replace-remote-form-target]')!
  const submitButton = container.querySelector('.js-new-codespace-submit-button')

  if (submitButton instanceof HTMLInputElement) {
    submitButton.disabled = true
  }

  form.classList.remove('is-error')
  form.classList.add('is-loading')

  try {
    if (submitButton) {
      submitButton.setAttribute('disabled', 'true')
    }

    const response = await wants.html()
    if (response.status !== 200) {
      resetNewCodespacePageWithResponseError()
    }
    replaceAndRefocus(container, response.html)
    if (container.getAttribute('data-allow-update-url') === 'true') {
      const newFormData = new FormData(document.querySelector('form.js-new-codespace-form') as HTMLFormElement)
      updateNewCodespaceUrl(newFormData)
    }
  } catch (e) {
    resetNewCodespacePageWithResponseError()
    throw e // We still want this to wind up in telemetry
  }
})

export function replaceAndRefocus(target: HTMLElement, partial: DocumentFragment) {
  const partialContent = partial.querySelector('*')

  const focusedElement = target.ownerDocument.activeElement

  let elementToRefocus: HTMLElement | null = null

  if (focusedElement instanceof HTMLElement && partialContent instanceof Element) {
    const selector = generateCssSelectorForElement(focusedElement)
    const candidates = Array.from(partialContent.querySelectorAll<HTMLElement>(selector))

    if (candidates.length === 1) {
      elementToRefocus = candidates[0] ?? null
    } else if (candidates.length > 1) {
      const focusedAria = focusedElement.getAttribute('aria-describedby')
      if (focusedAria) {
        const focusedAriaIds = focusedAria.split(/\s+/).filter(Boolean)

        for (const candidate of candidates) {
          const candidateAria = candidate.getAttribute('aria-describedby')
          if (!candidateAria) continue

          const candidateAriaIds = candidateAria.split(/\s+/).filter(Boolean)
          if (focusedAriaIds.some(id => candidateAriaIds.includes(id))) {
            elementToRefocus = candidate
            break
          }
        }

        if (!elementToRefocus) {
          elementToRefocus = candidates[0] ?? null
        }
      } else {
        elementToRefocus = candidates[0] ?? null
      }
    }
  }

  target.replaceWith(partial)

  if (elementToRefocus instanceof HTMLElement) {
    elementToRefocus.focus()
  }
}

function generateCssSelectorForElement(element: HTMLElement) {
  const tagName = element.tagName.toLowerCase()
  if (element.hasAttribute('id')) {
    const id = element.id
    const timestampPattern = /^(.+)-\d+$/
    // UUID pattern supports both uppercase and lowercase hex characters
    const uuidPattern = /^(.+)-[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}(-.*)?$/
    const match = id.match(timestampPattern)
    const uuidMatch = id.match(uuidPattern)

    if (match) {
      const prefix = match[1]
      return `${tagName}[id^="${prefix}"]`
    }
    if (uuidMatch) {
      const prefix = uuidMatch[1]
      const suffix = uuidMatch[2] || '' // Handle optional suffix like "-button"
      if (suffix) {
        // For IDs like "select-panel-{uuid}-button", use both prefix and suffix
        return `${tagName}[id^="${prefix}"][id$="${suffix}"]`
      } else {
        // For IDs like "select-panel-{uuid}", just use prefix
        return `${tagName}[id^="${prefix}"]`
      }
    }
    return `${tagName}#${id}`
  }
  const classNames = element.hasAttribute('class') ? `.${element.className.split(' ').join('.')}` : ''
  return `${tagName}${classNames}`
}

function resetNewCodespacePageWithResponseError() {
  const url = new URL(document.location.href, window.location.origin)
  const urlParams = new URLSearchParams(url.search)
  urlParams.set('response_error', 'true')
  window.location.replace(`${window.location.pathname}?${urlParams.toString()}`)
}

type LoadingState = null | 'provisioning' | 'provisioned' | 'stuck' | 'failed'

let loadingState: LoadingState = null

function advanceLoadingState(state: LoadingState) {
  loadingState = state

  if (state !== null) {
    const loadingSteps = document.querySelector<HTMLDivElement>('.js-codespace-loading-steps')!
    loadingSteps.setAttribute('data-current-state', loadingState as string)
  }
}

observe('.js-codespace-loading-steps', {
  constructor: HTMLElement,
  add: el => {
    const currentState = el.getAttribute('data-current-state') as LoadingState
    if (currentState) advanceLoadingState(currentState)
  },
})

observe('.js-codespace-advance-state', {
  constructor: HTMLElement,
  add: el => {
    const targetState = el.getAttribute('data-state') as LoadingState
    if (targetState) advanceLoadingState(targetState)
  },
})

observe('.js-auto-submit-form', {
  constructor: HTMLFormElement,
  initialize: requestSubmit,
})

observe('.js-workbench-form-container', {
  constructor: HTMLElement,
  add: () => {
    const form = document.querySelector('.js-workbench-form-container form') as HTMLFormElement
    requestSubmit(form)
  },
})
